import mapboxgl from 'mapbox-gl'
import React, { memo, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { ThemeProvider, useTheme } from 'styled-components'
import {
  FeatureCollectionWithOptionalInfoAndStyle,
  FeatureWithInfoAndStyle,
} from '~/components/mapContent/utils/geoAndStatsToFeatureCollection'
import serial from '~/prix/utils/serial'
import { MapContext } from './mapContext'
import addUpperLayerEventListener from './upperLayerEventListener'

interface GeoJSONProps {
  geoJson: FeatureCollectionWithOptionalInfoAndStyle
  customCirclePaint?: mapboxgl.CirclePaint
  customLinePaint?: mapboxgl.LinePaint
  customPolygonPaint?: mapboxgl.FillPaint
  renderInfoWindow?: (props: { feature: FeatureWithInfoAndStyle }) => JSX.Element
  onClick?: (event: mapboxgl.MapMouseEvent, feature: FeatureWithInfoAndStyle) => void
}

const ids = serial()

const GeoJSON = ({ geoJson, renderInfoWindow, onClick }: GeoJSONProps) => {
  const mapContext = useContext(MapContext)
  const theme = useTheme()

  if (!mapContext) {
    throw new Error('GeoJSON must be used within a Map')
  }

  const { map } = mapContext

  const firstRender = useRef(false)
  const id = useRef(ids.next())
  const idSuffix = useMemo(() => `-geojson-${id.current}`, [id])
  const sourceId = useMemo(() => `source${idSuffix}`, [id])

  useEffect(() => {
    if (geoJson.features.length === 0 || firstRender.current) return

    try {
      if (!map || !map.getStyle()) {
        return
      }
    } catch {
      return
    }

    map.addSource(sourceId, {
      type: 'geojson',
      data: geoJson as GeoJSON.FeatureCollection<GeoJSON.Geometry>,
    })

    const beforeLayerId = 'road-label'

    map.addLayer(
      {
        id: 'points' + idSuffix,
        type: 'circle',
        source: sourceId,
        paint: {
          'circle-radius': ['get', 'radius'],
          'circle-color': ['get', 'fillColor'],
        },
        filter: ['==', '$type', 'Point'],
      },
      beforeLayerId,
    )

    map.addLayer(
      {
        id: 'lines-hitbox' + idSuffix,
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': 'rgba(0, 0, 0, 0)',
          'line-width': 12,
        },
        filter: ['==', '$type', 'LineString'],
      },
      beforeLayerId,
    )

    map.addLayer(
      {
        id: 'lines' + idSuffix,
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': ['get', 'strokeColor'],
          'line-width': ['get', 'strokeWeight'],
          'line-opacity': ['get', 'strokeOpacity'],
        },
        filter: ['==', '$type', 'LineString'],
      },
      beforeLayerId,
    )

    map.addLayer(
      {
        id: 'polygons' + idSuffix,
        type: 'fill',
        source: sourceId,
        paint: {
          'fill-color': ['get', 'fillColor'],
          'fill-opacity': ['get', 'fillOpacity'],
        },
        filter: ['==', '$type', 'Polygon'],
      },
      beforeLayerId,
    )

    map.addLayer(
      {
        id: 'polygon-borders' + idSuffix,
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': ['get', 'strokeColor'],
          'line-width': ['get', 'strokeWeight'],
          'line-opacity': ['get', 'strokeOpacity'],
        },
        filter: ['==', '$type', 'Polygon'],
      },
      beforeLayerId,
    )

    //marca que renderizou a primeira vez
    firstRender.current = true
  }, [map, geoJson, firstRender])

  useEffect(() => {
    return () => {
      try {
        if (!map || !map.getStyle()) {
          return
        }
      } catch {
        return
      }

      if (map.getLayer('points' + idSuffix)) map.removeLayer('points' + idSuffix)
      if (map.getLayer('lines' + idSuffix)) map.removeLayer('lines' + idSuffix)
      if (map.getLayer('lines-hitbox' + idSuffix)) map.removeLayer('lines-hitbox' + idSuffix)
      if (map.getLayer('polygons' + idSuffix)) map.removeLayer('polygons' + idSuffix)
      if (map.getLayer('polygon-borders' + idSuffix)) map.removeLayer('polygon-borders' + idSuffix)
      if (map.getSource(sourceId)) map.removeSource(sourceId)
    }
  }, [map])

  useEffect(() => {
    try {
      if (!firstRender.current || !map || !map.getStyle()) {
        return
      }
    } catch {
      return
    }

    const source = map.getSource(sourceId) as mapboxgl.GeoJSONSource
    if (!source) return

    source.setData(geoJson as GeoJSON.FeatureCollection<GeoJSON.Geometry>)

    const layers = [
      'points' + idSuffix,
      'lines' + idSuffix,
      'lines-hitbox' + idSuffix,
      'polygons' + idSuffix,
      'polygon-borders' + idSuffix,
    ]

    const popup = new mapboxgl.Popup()

    const handleClick = (event: mapboxgl.MapMouseEvent) => {
      const features = map.queryRenderedFeatures(event.point, { layers })
      if (!features.length) return

      const feature = (
        features[0] as unknown as {
          toJSON: () => FeatureWithInfoAndStyle
        }
      ).toJSON()

      if (renderInfoWindow) {
        const div = document.createElement('div')
        div.className = 'prix-info-window'
        const root = createRoot(div)

        root.render(<ThemeProvider theme={theme}>{renderInfoWindow({ feature })}</ThemeProvider>)

        popup.setLngLat(event.lngLat)
        popup.setDOMContent(div)
        popup.addTo(map)
      }

      if (onClick) {
        onClick(event, feature)
      }
    }

    const offClick = addUpperLayerEventListener(map, 'click', layers, handleClick)

    return () => {
      try {
        if (!map || !map.getStyle()) {
          return
        }
      } catch {
        return
      }

      popup.remove()
      offClick()
    }
  }, [map, geoJson, firstRender, onClick, renderInfoWindow, theme])

  return null
}

export default memo(GeoJSON)
